import { memo, useCallback, useMemo } from "react";
import { useField } from "formik";
import type { NumberFormatValues, SourceInfo } from "react-number-format";
import { useTranslation } from "react-i18next";
import NumberInput from "../NumberInput";
import type { NumberInputProps } from "../NumberInput";

type FormNumberInputProps = NumberInputProps & { name: string };

function FormNumberInput(props: FormNumberInputProps) {
  const { name, onValueChange, ...rest } = props;
  const [{ onChange, ...restField }, meta] = useField(name);
  const { t } = useTranslation();

  const hasError = Boolean(meta.touched && meta.error);

  const helperText = useMemo(() => {
    if (hasError) return t(meta.error as string);
    return rest.helperText;
  }, [hasError, rest.helperText, t, meta.error]);

  const handleValueChange = useCallback(
    (values: NumberFormatValues, sourceInfo: SourceInfo) => {
      onChange({ target: { name, value: values.floatValue ?? "" } });
      onValueChange?.(values, sourceInfo);
    },
    [onChange, onValueChange, name]
  );

  return (
    <NumberInput
      decimalScale={0}
      allowNegative={false}
      error={hasError}
      {...restField}
      {...rest}
      helperText={helperText}
      onValueChange={handleValueChange}
    />
  );
}

export default memo(FormNumberInput);
